import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Form, Dropdown, DropdownButton, FormControl, Spinner } from 'react-bootstrap';
import './BootstrapMultiSelect.css';

const BootstrapMultiSelect = ({ hiddenOptions, label, options, selectedEquipments, setSelectedEquipments }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const bodyHasDarkClass = document.body.classList.contains('dark-theme');
    const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');

    if (savedTheme) {
      return savedTheme;
    }

    return bodyHasDarkClass || htmlHasDarkClass ? 'dark' : 'light';
  });

  useEffect(() => {
    const handleThemeChange = () => {
      const bodyHasDarkClass = document.body.classList.contains('dark-theme');
      const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');
      const savedTheme = localStorage.getItem('theme');

      let detectedTheme = 'light';

      if (savedTheme) {
        detectedTheme = savedTheme;
      } else if (bodyHasDarkClass || htmlHasDarkClass) {
        detectedTheme = 'dark';
      }

      setTheme(detectedTheme);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Memoiza as opções filtradas para evitar cálculos desnecessários
  // Agora suporta lista de termos (ex: "item1, item2, item3" ou "BIMT-04071,BIMT-04072,BIMT-04073").
  // Faz correspondência caso o item contenha QUALQUER um dos termos informados (OR).
  const filteredOptions = useMemo(() => {
    const raw = searchTerm || '';
    const trimmed = raw.trim();
    if (!trimmed) return options;

    // Divide por vírgula, ponto e vírgula, quebras de linha e tabulação
    const terms = trimmed
      .split(/[,;\n\t]+/)
      .map(t => t.trim())
      .filter(Boolean);

    if (terms.length === 0) return options;

    const filtered = options.filter(option => {
      const opt = String(option).toLowerCase();
      // Verifica se algum termo corresponde (exato ou contém)
      const matches = terms.some(term => {
        const termLower = term.toLowerCase();
        return opt === termLower || opt.includes(termLower);
      });
      return matches;
    });

    return filtered;
  }, [options, searchTerm]);

  // Função de seleção otimizada com useCallback para evitar recriação
  const handleSelect = useCallback((eventKey) => {
    setSelectedEquipments((prevSelected) =>
      prevSelected.includes(eventKey)
        ? prevSelected.filter(item => item !== eventKey)
        : [...prevSelected, eventKey]
    );
  }, [setSelectedEquipments]);

  // Atualiza o termo de busca imediatamente sem debounce
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Limpa todas as seleções
  const handleClearAll = useCallback(() => {
    setSelectedEquipments([]);
  }, [setSelectedEquipments]);

  // Simula o carregamento inicial de dados ao abrir o dropdown
  const handleToggle = useCallback((isOpen) => {
    setShowDropdown(isOpen);
    if (isOpen) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false); // Simula a conclusão do carregamento
      }, 100);
    }
  }, []);

  // Memoiza o título para evitar recriação desnecessária
  const dropdownTitle = useMemo(() => {
    if (hiddenOptions) {
      return label;
    }
    if (selectedEquipments.length > 0) {
      return (
        <>
          {selectedEquipments.map((item, index) => (
            <div key={`selected-${index}-${item}`} style={{ whiteSpace: 'normal' }}>{item}</div>
          ))}
        </>
      );
    }
    return label;
  }, [hiddenOptions, label, selectedEquipments]);

  return (
    <div className="multiselect-container mt-1 mb-1">
      <Form.Group>
        <DropdownButton
          id="dropdown-multi-select"
          title={dropdownTitle}
          variant="success"
          show={showDropdown}
          onToggle={handleToggle}
        >
          <FormControl
            className="border border-secondary rounded"
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={handleSearch}
            style={{
              marginTop: '0.5rem',
              marginLeft: '0.5rem',
              marginRight: '0.5rem',
              marginBottom: 0,
              width: '93%'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="d-flex justify-content-between align-items-center" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
            <label
              className="multiselect-total-label mb-0"
              style={{
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Total: <strong>{filteredOptions.length}</strong>
              {selectedEquipments.length > 0 && (
                <span className="ms-1" style={{ color: '#198754' }}>
                  | Selecionados: <strong>{selectedEquipments.length}</strong>
                </span>
              )}
            </label>
            {selectedEquipments.length > 0 && (
              <button
                className="mt-1 mb-1 btn btn-sm btn-outline-warning"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                style={{
                  fontSize: '12px',
                  padding: '2px 8px'
                }}
                title="Limpar todas as seleções"
              >
                Limpar
              </button>
            )}
          </div>
          <hr className="m-0" />
          <div className="multiselect-dropdown-list">
            {loading ? (
              <div className="multiselect-loading d-flex justify-content-center align-items-center pt-3 pb-2">
                <Spinner animation="border" variant="success" size="sm" />
                <span className="ms-2">Carregando...</span>
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <Dropdown.Item
                  key={index}
                  active={selectedEquipments.includes(option)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option);
                  }}
                  className="multiselect-dropdown-item"
                  style={{
                    cursor: 'pointer',
                    fontSize: '14px',
                    backgroundColor: selectedEquipments.includes(option)
                      ? (theme === 'dark' ? '#198754' : '#198754')
                      : 'transparent',
                    color: selectedEquipments.includes(option)
                      ? '#fff'
                      : (theme === 'dark' ? '#fff' : '#000')
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedEquipments.includes(option)) {
                      e.currentTarget.style.backgroundColor = '#157347';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedEquipments.includes(option)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = theme === 'dark' ? '#fff' : '#000';
                    }
                  }}
                >
                  {option}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled className="text-center">
                Nenhum item encontrado
              </Dropdown.Item>
            )}
          </div>
        </DropdownButton>
      </Form.Group>
    </div>
  );
};

export default BootstrapMultiSelect;
